import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Stack,
  Grid,
  Alert,
} from "@mui/material";

const headingFont = `"Bodoni Moda", serif`;

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

const DHA_MAP_SRC =
  "https://maps.google.com/maps?q=Haroon%20Sharif%20Jewellers%20DHA%20Y-block&t=&z=15&ie=UTF8&iwloc=&output=embed";

const ContactPage: React.FC = () => {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleChange =
    (field: keyof typeof form) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSuccessMsg(null);
    setErrorMsg(null);

    if (!form.email || !form.message) {
      setErrorMsg("Please provide at least your email and your message.");
      return;
    }

    try {
      setSubmitting(true);

      const response = await fetch(`${API_BASE}/contact_messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          contact: {
            first_name: form.firstName,
            last_name: form.lastName,
            email: form.email,
            phone: form.phone,
            message: form.message,
          },
        }),
      });

      if (!response.ok) {
        const text = await response.text();
        let data = {};
        try {
          data = JSON.parse(text);
        } catch {
          /* ignore */
        }
        throw new Error((data as any).error || "Failed to send message.");
      }

      setSuccessMsg(
        "Thank you for your message. We will get back to you soon.",
      );
      setForm({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        message: "",
      });
    } catch (err: any) {
      setErrorMsg(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "#ffffff",
        minHeight: "100vh",
        pt: { xs: 8, md: 10 },
        pb: { xs: 10, md: 12 },
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 6, md: 8 }}>
          {/* LEFT SIDE FORM */}
          <Grid item xs={12} md={6}>
            <Stack spacing={3} sx={{ maxWidth: 520 }}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    letterSpacing: ".2em",
                    fontSize: ".78rem",
                    textTransform: "uppercase",
                  }}
                >
                  Leave a message
                </Typography>

                <Typography
                  variant="h4"
                  sx={{
                    fontFamily: headingFont,
                    fontWeight: 700,
                    letterSpacing: ".04em",
                    textTransform: "uppercase",
                    mt: 1,
                  }}
                >
                  Contact Us
                </Typography>
              </Box>

              <Stack spacing={1.5}>
                <Typography
                  sx={{
                    fontSize: ".95rem",
                    lineHeight: 1.7,
                    color: "rgba(0,0,0,0.7)",
                  }}
                >
                  Fill out our Contact Form or email us and we will get back to
                  you within 24 hours.
                </Typography>
                <Typography
                  sx={{
                    fontSize: ".9rem",
                    lineHeight: 1.6,
                    color: "rgba(0,0,0,0.6)",
                  }}
                >
                  We are available Sunday through Thursday, 10am to 5pm,
                  excluding holidays.
                </Typography>
              </Stack>

              {successMsg && (
                <Alert
                  severity="success"
                  onClose={() => setSuccessMsg(null)}
                  sx={{ mt: 1 }}
                >
                  {successMsg}
                </Alert>
              )}

              {errorMsg && (
                <Alert
                  severity="error"
                  onClose={() => setErrorMsg(null)}
                  sx={{ mt: 1 }}
                >
                  {errorMsg}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit} noValidate>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="First name"
                      variant="standard"
                      fullWidth
                      value={form.firstName}
                      onChange={handleChange("firstName")}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Last name"
                      variant="standard"
                      fullWidth
                      value={form.lastName}
                      onChange={handleChange("lastName")}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Email"
                      type="email"
                      variant="standard"
                      fullWidth
                      required
                      value={form.email}
                      onChange={handleChange("email")}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Phone"
                      variant="standard"
                      fullWidth
                      value={form.phone}
                      onChange={handleChange("phone")}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label="How can we help?"
                      variant="standard"
                      fullWidth
                      required
                      multiline
                      minRows={4}
                      value={form.message}
                      onChange={handleChange("message")}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} sx={{ mt: 1 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      disabled={submitting}
                      sx={{
                        borderRadius: 0,
                        textTransform: "uppercase",
                        letterSpacing: ".15em",
                        py: 1.5,
                        fontSize: ".85rem",
                        boxShadow: "none",
                        backgroundColor: "#000",
                        "&:hover": {
                          backgroundColor: "#222",
                          boxShadow: "none",
                        },
                      }}
                    >
                      {submitting ? "Sending..." : "Send Message"}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Stack>
          </Grid>

          {/* RIGHT SIDE MAP */}
          <Grid item xs={12} md={6}>
            <Stack spacing={3} sx={{ height: "100%" }}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    letterSpacing: ".2em",
                    fontSize: ".78rem",
                    textTransform: "uppercase",
                  }}
                >
                  We are here
                </Typography>

                <Typography
                  variant="h4"
                  sx={{
                    fontFamily: headingFont,
                    fontWeight: 700,
                    letterSpacing: ".04em",
                    textTransform: "uppercase",
                    mt: 1,
                  }}
                >
                  Our Office
                </Typography>
              </Box>

              <Box
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                  boxShadow: "0 16px 40px rgba(0,0,0,0.15)",
                  flexGrow: 1,
                  minHeight: { xs: 260, md: 320 },
                }}
              >
                <Box
                  component="iframe"
                  src={DHA_MAP_SRC}
                  loading="lazy"
                  sx={{
                    border: 0,
                    width: "100%",
                    height: "100%",
                  }}
                />
              </Box>

              <Stack spacing={0.5}>
                <Typography
                  sx={{
                    textTransform: "uppercase",
                    letterSpacing: ".16em",
                    fontSize: ".7rem",
                    color: "rgba(0,0,0,0.7)",
                  }}
                >
                  DHA BRANCH
                </Typography>

                <Typography sx={{ fontSize: ".98rem", lineHeight: 1.7 }}>
                  Haroon Sharif Jewellers
                </Typography>

                <Typography sx={{ fontSize: ".98rem", lineHeight: 1.7 }}>
                  DHA Y-Block 81-Y, Phase 3, Lahore
                </Typography>

                <Typography sx={{ fontSize: ".95rem", mt: 0.5 }}>
                  Phone: 042-35741151
                </Typography>
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ContactPage;
